import { api, ApiError, ApiNetworkError } from '../api.js';
import { loadOperatorSession, clearOperatorSession } from '../config.js';
import { escapeHtml, formatTime, qs } from '../dom.js';
import type { AsnDashboardSummary } from '../types.js';

export async function renderAsnList(container: HTMLElement): Promise<void> {
  const operatorSession = loadOperatorSession();

  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `
    <div class="topbar">
      <h1>Receiving</h1>
      <div class="actions-row" style="gap:0.5rem; flex-wrap:wrap;">
        <button class="ghost" id="home">Home</button>
        <button class="ghost" id="refresh">Refresh</button>
      </div>
    </div>
    ${operatorSession ? `<p class="hint">Logged in as <strong>${escapeHtml(operatorSession.name)}</strong> · <button class="link-button" id="logout">Log out</button></p>` : ''}
    <input type="search" id="asnSearch" placeholder="Search PO or ASN number" autocomplete="off" autocapitalize="off" spellcheck="false" />
    <div class="chips" id="asnChips"></div>
    <p class="hint" id="asnSummary"></p>
    <div id="body"><p class="hint center">Loading…</p></div>
  `;
  container.appendChild(div);

  qs<HTMLButtonElement>(div, '#home').addEventListener('click', () => {
    location.hash = '#/';
  });
  qs<HTMLButtonElement>(div, '#refresh').addEventListener('click', () => load());
  div.querySelector<HTMLButtonElement>('#logout')?.addEventListener('click', () => {
    void (async () => {
      try {
        await api.logout();
      } catch {
        // Best-effort — clear locally regardless (see settings.ts's logout for the same reasoning).
      }
      clearOperatorSession();
      location.hash = '#/login';
    })();
  });

  // The list is fetched once per load and filtered in the browser (search + chips), so typing
  // never waits on the server.
  let allAsns: AsnDashboardSummary[] = [];
  let activeFilter: AsnFilter = loadSavedFilter();
  const searchInput = qs<HTMLInputElement>(div, '#asnSearch');
  const chipsEl = qs<HTMLDivElement>(div, '#asnChips');
  const summaryEl = qs<HTMLParagraphElement>(div, '#asnSummary');

  function renderChips(): void {
    chipsEl.innerHTML = ASN_FILTERS.map((f) => {
      const n = allAsns.filter((a) => matchesFilter(a, f.key)).length;
      return `<button type="button" class="chip${f.key === activeFilter ? ' active' : ''}" data-filter="${f.key}">${f.label} (${n})</button>`;
    }).join('');
    chipsEl.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        activeFilter = btn.dataset.filter as AsnFilter;
        try {
          sessionStorage.setItem('wr.asnFilter', activeFilter);
        } catch {
          // storage unavailable — the in-memory choice still works
        }
        renderFiltered();
      });
    });
  }

  function renderFiltered(): void {
    const body = qs<HTMLDivElement>(div, '#body');
    const term = searchInput.value.trim().toLowerCase();
    const shown = allAsns.filter((a) => matchesFilter(a, activeFilter) && (!term || a.poNumber.toLowerCase().includes(term) || String(a.mintsoftAsnId ?? '').includes(term)));
    renderChips();
    const active = allAsns.filter((a) => a.sessionStatus === 'RECEIVING' || a.sessionStatus === 'READY_FOR_CONFIRMATION').length;
    summaryEl.textContent = `${shown.length} shown · ${active} being received · ${allAsns.length} total`;
    body.innerHTML = '';
    body.appendChild(renderList(shown, allAsns.length === 0));
    wireRowActions(body);
  }

  searchInput.addEventListener('input', () => renderFiltered());

  async function load(): Promise<void> {
    const body = qs<HTMLDivElement>(div, '#body');
    body.innerHTML = '<p class="hint center">Loading…</p>';
    try {
      allAsns = await api.getAsns();
      renderFiltered();
    } catch (err) {
      body.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div>`;
    }
  }

  function wireRowActions(body: HTMLElement): void {
    body.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const asnId = btn.dataset.asnId!;
        const sessionId = btn.dataset.sessionId;
        if (btn.dataset.action === 'resume' && sessionId) {
          location.hash = `#/scan/${sessionId}`;
          return;
        }
        if (btn.dataset.action === 'review' && sessionId) {
          location.hash = `#/review/${sessionId}`;
          return;
        }
        // 'start' — no session yet, create/resume one via the ASN id.
        btn.disabled = true;
        btn.textContent = 'Starting…';
        try {
          const session = await api.startOrResumeReceiving(asnId);
          location.hash = `#/scan/${session.id}`;
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Start Receiving';
          alert(describeError(err));
        }
      });
    });
  }

  await load();
}

type AsnFilter = 'ALL' | 'ACTIVE' | 'NOT_STARTED' | 'DONE';
const ASN_FILTERS: { key: AsnFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Receiving' },
  { key: 'NOT_STARTED', label: 'Not started' },
  { key: 'DONE', label: 'Done' },
];

function loadSavedFilter(): AsnFilter {
  try {
    const saved = sessionStorage.getItem('wr.asnFilter');
    if (saved === 'ALL' || saved === 'ACTIVE' || saved === 'NOT_STARTED' || saved === 'DONE') return saved;
  } catch {
    // ignore — default below
  }
  return 'ALL';
}

function matchesFilter(a: AsnDashboardSummary, f: AsnFilter): boolean {
  if (f === 'ALL') return true;
  if (f === 'NOT_STARTED') return !a.sessionStatus;
  if (f === 'ACTIVE') return a.sessionStatus === 'RECEIVING' || a.sessionStatus === 'READY_FOR_CONFIRMATION';
  return a.sessionStatus === 'CONFIRMED' || a.sessionStatus === 'COMPLETED';
}

function renderList(asns: AsnDashboardSummary[], noneRegistered = false): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'list';
  if (asns.length === 0) {
    wrap.innerHTML = noneRegistered
      ? '<div class="card"><p class="hint">No ASNs registered yet. They appear here once created — via the buyer-order upload or a manual link.</p></div>'
      : '<div class="card"><p class="hint">No ASNs match. Clear the search or pick another filter.</p></div>';
    return wrap;
  }
  for (const asn of asns) {
    wrap.appendChild(renderRow(asn));
  }
  return wrap;
}

function renderRow(asn: AsnDashboardSummary): HTMLElement {
  const row = document.createElement('div');
  row.className = 'asn-row';
  const { label, cls } = statusBadge(asn);
  const pct = asn.expectedQuantity > 0 ? Math.min(100, Math.round((asn.receivedQuantity / asn.expectedQuantity) * 100)) : 0;
  const variance = asn.variance !== 0 ? ` · variance ${asn.variance > 0 ? '+' : ''}${asn.variance}` : '';
  row.innerHTML = `
    <div class="po">${escapeHtml(asn.poNumber)}</div>
    <div class="meta">Mintsoft ASN ${asn.mintsoftAsnId ?? '—'} · updated ${escapeHtml(formatTime(asn.updatedAt))}</div>
    <span class="badge ${cls}">${label}</span>
    <div class="meta">Expected ${asn.expectedQuantity} · Received ${asn.receivedQuantity}${variance}</div>
    ${asn.sessionStatus ? `<div class="asn-bar${pct >= 100 ? ' full' : ''}"><div style="width:${pct}%"></div></div><div class="meta">${pct}% received</div>` : ''}
  `;
  const btn = document.createElement('button');
  btn.dataset.asnId = asn.asnId;
  if (asn.sessionId) btn.dataset.sessionId = asn.sessionId;
  if (!asn.sessionStatus) {
    btn.className = 'primary';
    btn.textContent = 'Start Receiving';
    btn.dataset.action = 'start';
  } else if (asn.sessionStatus === 'RECEIVING' || asn.sessionStatus === 'READY_FOR_CONFIRMATION') {
    btn.className = 'primary';
    btn.textContent = 'Resume Scanning';
    btn.dataset.action = 'resume';
  } else {
    btn.className = 'ghost';
    btn.textContent = 'View Review';
    btn.dataset.action = 'review';
  }
  row.appendChild(btn);
  return row;
}

function statusBadge(asn: AsnDashboardSummary): { label: string; cls: string } {
  if (!asn.sessionStatus) return { label: 'Not started', cls: 'pending' };
  if (asn.sessionStatus === 'RECEIVING') return { label: 'Receiving', cls: 'pending' };
  if (asn.sessionStatus === 'READY_FOR_CONFIRMATION') return { label: 'Ready to confirm', cls: 'short' };
  if (asn.sessionStatus === 'CONFIRMED') return { label: 'Confirmed', cls: 'exact' };
  return { label: 'Completed', cls: 'exact' };
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach the server — check your connection or Settings.';
  if (err instanceof ApiError) return `${err.message} (${err.status})`;
  return String(err);
}
